import { firstValueFrom, map } from 'rxjs'
import { Environment } from 'environment.js'
import { tracedAjax } from '../Sentry.js'
import type { UnspinnedCount } from '../../Generated/UnspinnedCount.ts'
import type { SpinSummary } from '../../Generated/SpinSummary.ts'
import type { SpinLineage } from '../../Generated/SpinLineage.ts'
import type { SpinScopeStatus } from '../../Generated/SpinScopeStatus.ts'
import type { SpinScopeState } from '../../Generated/SpinScopeState.ts'
import type { SpinChangePoint } from '../../Generated/SpinChangePoint.ts'
import type { SpinShowcaseEntry } from '../../Generated/SpinShowcaseEntry.ts'

export type { UnspinnedCount, SpinSummary, SpinLineage, SpinScopeStatus, SpinScopeState, SpinChangePoint, SpinShowcaseEntry }

/** A fresh SPIN state with the tag to revalidate it with (null when the backend sent none), or a 304. */
export type SpinStateFetchResult = { kind: 'fresh'; state: SpinScopeState; etag: string | null } | { kind: 'unchanged' }

/**
 * The scope's SPIN state in one body: per order whether its SPINs exist, and per algorithm with coverage its
 * change-points and latest unclustered count. With [etag], a `304` answers `unchanged`.
 */
export const fetchSpinState = (scopeId: string, etag?: string | null): Promise<SpinStateFetchResult> =>
  firstValueFrom(
    tracedAjax<SpinScopeState>({
      url: `${Environment.VIS_URL}/spin/${scopeId}/state`,
      method: 'GET',
      withCredentials: true,
      headers: etag ? { 'If-None-Match': etag } : {}
    }).pipe(
      map((r): SpinStateFetchResult =>
        r.status === 304
          ? { kind: 'unchanged' }
          : { kind: 'fresh', state: r.response ?? { status: {} }, etag: r.xhr?.getResponseHeader('ETag') ?? null }
      )
    )
  )

/**
 * The public, cheap definition of one SPIN (id, algorithm, when derived, member count, resolved
 * taxonomy) — backs the shareable `/spins/{id}` page. Lives on the main API (not the viz pods).
 * Resolves to null on 404 (unknown SPIN).
 */
export const fetchSpinSummary = (spinId: string): Promise<SpinSummary | null> =>
  firstValueFrom(
    tracedAjax<SpinSummary>({
      url: `${Environment.API_URL}/api/spins/${encodeURIComponent(spinId)}`,
      method: 'GET',
      withCredentials: true
    }).pipe(map((r) => r.response ?? null))
  ).catch(() => null)

/**
 * How many of the scope's OTUs have no SPIN under `algorithmType` as of an explicit `atTime` — i.e. the rows the SPIN
 * cube drops. The latest count comes with {@link fetchSpinState}.
 */
export const fetchSpinUnspindCount = (scopeId: string, algorithmType: string, atTime: string): Promise<UnspinnedCount | null> => {
  const at = atTime ? `&atTime=${encodeURIComponent(atTime)}` : ''
  return firstValueFrom(
    tracedAjax<UnspinnedCount>({
      url: `${Environment.VIS_URL}/spin/${scopeId}/unspind-count?algorithmType=${encodeURIComponent(algorithmType)}${at}`,
      method: 'GET',
      withCredentials: true
    }).pipe(map((r) => r.response ?? null))
  )
}

/** The SPIN's predecessor chain, newest first; null when the SPIN does not exist or the call failed. */
export const fetchSpinLineage = (spinId: string): Promise<SpinLineage | null> =>
  firstValueFrom(
    tracedAjax<SpinLineage>({
      url: `${Environment.API_URL}/api/spins/${encodeURIComponent(spinId)}/lineage`,
      method: 'GET',
      withCredentials: true
    }).pipe(map((r) => r.response ?? null))
  ).catch(() => null)

/** Example SPINs for the lookup page; empty when none are picked yet or the call failed. */
export const fetchSpinShowcase = (): Promise<SpinShowcaseEntry[]> =>
  firstValueFrom(
    tracedAjax<SpinShowcaseEntry[]>({
      url: `${Environment.API_URL}/api/spins/showcase`,
      method: 'GET',
      withCredentials: true
    }).pipe(map((r) => r.response ?? []))
  ).catch(() => [])
